/**
 * TeamManager.js — the 👥 Team window on the Shift Planner.
 *
 *  • Arrange the team order (drag the ⠿ handle, or ↑ ↓) — used everywhere on the Shifts page.
 *  • Deactivate someone who left / is on leave (owner & admin): hidden from planning, history
 *    kept, and their upcoming shifts can be removed in the same step (the parent offers Undo).
 *  • Reactivate from the Inactive list.
 *
 * Persistence lives in the parent (ShiftDashboard → teamSettings.js); this is UI only.
 */
import React, { useState, useRef, useEffect } from 'react';

const MANAGER_ROLES = ['manager', 'admin', 'owner'];

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}
function fmtDay(iso) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
const nameOf = s => s.name || s.email || 'Staff';

export default function TeamManager({
  staff, inactive, canDeactivate, saveState,
  onReorder, loadUpcoming, onDeactivate, onReactivate, onClose,
}) {
  const active = staff.filter(s => !inactive[s.uid]);
  const inactiveList = staff.filter(s => inactive[s.uid]);
  const [drag, setDrag] = useState(null);       // { from, before } while dragging a handle
  const [confirm, setConfirm] = useState(null); // { uid, upcoming: null | shifts[], remove, busy }
  const listRef = useRef(null);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !confirm?.busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, confirm]);

  const ids = active.map(s => s.uid);
  const move = (from, to) => {
    if (to < 0 || to >= ids.length || to === from) return;
    const next = [...ids];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x);
    onReorder(next);
  };

  // Pointer-based drag (works with mouse, pen and touch — HTML5 drag-and-drop doesn't on phones)
  const dropIndexAt = clientY => {
    const rows = [...listRef.current.querySelectorAll('[data-row]')];
    const i = rows.findIndex(r => { const b = r.getBoundingClientRect(); return clientY < b.top + b.height / 2; });
    return i === -1 ? rows.length : i;
  };
  const onHandleDown = (e, from) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({ from, before: from });
  };
  const onHandleMove = e => {
    if (!drag) return;
    const before = dropIndexAt(e.clientY);
    if (before !== drag.before) setDrag(d => ({ ...d, before }));
  };
  const onHandleUp = () => {
    if (!drag) return;
    const { from, before } = drag;
    setDrag(null);
    move(from, before > from ? before - 1 : before);
  };

  const sortAZ = () => onReorder([...active].sort((a, b) => nameOf(a).localeCompare(nameOf(b))).map(s => s.uid));
  const managersFirst = () => onReorder([
    ...active.filter(s => MANAGER_ROLES.includes(s.role)),
    ...active.filter(s => !MANAGER_ROLES.includes(s.role)),
  ].map(s => s.uid));

  const startDeactivate = async emp => {
    setConfirm({ uid: emp.uid, upcoming: null, remove: true, busy: false });
    const upcoming = await loadUpcoming(emp.uid);
    setConfirm(c => (c && c.uid === emp.uid ? { ...c, upcoming } : c));
  };
  const doDeactivate = async emp => {
    if (!confirm || confirm.busy || confirm.upcoming === null) return;
    setConfirm(c => ({ ...c, busy: true }));
    const ok = await onDeactivate(emp, confirm.remove ? confirm.upcoming : []);
    setConfirm(ok ? null : c => ({ ...c, busy: false }));
  };

  const avatarStyle = s => ({
    background: MANAGER_ROLES.includes(s.role)
      ? 'linear-gradient(135deg,#06b6d4,#0891b2)' : 'linear-gradient(135deg,#f59e0b,#d97706)',
  });

  return (
    <div className="shift-edit-overlay" onClick={() => !confirm?.busy && onClose()}>
      <div className="sp-modal tm" role="dialog" aria-modal="true" aria-label="Team" onClick={e => e.stopPropagation()}>
        <div className="sp-modal__header">
          <div>
            <h2 className="sp-modal__title">👥 Team</h2>
            <p className="sp-modal__sub">The order here is used across Shifts — planner, preview, WhatsApp, roster.</p>
          </div>
          <button type="button" className="sp-modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="tm-bar">
          <span className="tm-bar__lbl">Active · {active.length}</span>
          <div className="tm-bar__actions">
            <button type="button" className="tm-quick" onClick={sortAZ} title="Put everyone in A–Z order">A–Z</button>
            <button type="button" className="tm-quick" onClick={managersFirst} title="Move managers to the top, keeping the rest in order">Managers first</button>
            <span className={`tm-save tm-save--${saveState}`} role="status">
              {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved ✓'
                : saveState === 'denied' ? 'Not saved — no permission yet' : saveState === 'error' ? "Couldn't save — try again" : ''}
            </span>
          </div>
        </div>

        <ul className={`tm-list${drag ? ' tm-list--dragging' : ''}`} ref={listRef}>
          {active.map((s, i) => {
            const isConfirming = confirm?.uid === s.uid;
            const up = confirm?.upcoming;
            const cls = [
              'tm-row',
              drag?.from === i && 'tm-row--dragging',
              drag && drag.before === i && drag.from !== i && drag.from !== i - 1 && 'tm-row--drop-before',
              drag && i === active.length - 1 && drag.before === active.length && drag.from !== i && 'tm-row--drop-after',
            ].filter(Boolean).join(' ');
            return (
              <React.Fragment key={s.uid}>
                <li data-row className={cls}>
                  <button
                    type="button" className="tm-handle" aria-label={`Drag to move ${nameOf(s)}`} title="Drag to reorder"
                    onPointerDown={e => onHandleDown(e, i)} onPointerMove={onHandleMove}
                    onPointerUp={onHandleUp} onPointerCancel={() => setDrag(null)}
                  >⠿</button>
                  <span className="tm-pos">{i + 1}</span>
                  <span className="tm-avatar" style={avatarStyle(s)}>{initials(s.name)}</span>
                  <span className="tm-info">
                    <span className="tm-name">{nameOf(s)}</span>
                    <span className="tm-role">{s.role}</span>
                  </span>
                  <span className="tm-moves">
                    <button type="button" className="tm-move" onClick={() => move(i, i - 1)} disabled={i === 0} aria-label={`Move ${nameOf(s)} up`}>↑</button>
                    <button type="button" className="tm-move" onClick={() => move(i, i + 1)} disabled={i === active.length - 1} aria-label={`Move ${nameOf(s)} down`}>↓</button>
                  </span>
                  {canDeactivate && !isConfirming && (
                    <button type="button" className="tm-deact" onClick={() => startDeactivate(s)}>Deactivate</button>
                  )}
                </li>
                {isConfirming && (
                  <li className="tm-confirm" role="alert">
                    <strong className="tm-confirm__q">Deactivate {nameOf(s)}?</strong>
                    <ul className="tm-confirm__facts">
                      <li>Hidden from the planner, availability, roster and WhatsApp lists</li>
                      <li>Past shifts and hours stay in reports</li>
                      <li>Can't clock in until reactivated</li>
                    </ul>
                    {up === null ? (
                      <div className="tm-confirm__shifts">Checking upcoming shifts…</div>
                    ) : up.length ? (
                      <label className="tm-confirm__shifts tm-confirm__shifts--check">
                        <input type="checkbox" checked={confirm.remove} onChange={e => setConfirm(c => ({ ...c, remove: e.target.checked }))} />
                        Also remove {up.length} upcoming shift{up.length > 1 ? 's' : ''} ({fmtDay(up[0].date)}
                        {up.length > 1 && up[up.length - 1].date !== up[0].date ? ` – ${fmtDay(up[up.length - 1].date)}` : ''})
                      </label>
                    ) : (
                      <div className="tm-confirm__shifts">No upcoming shifts.</div>
                    )}
                    <p className="tm-confirm__note">To also stop them signing in, use Block on the Sessions page.</p>
                    <div className="shift-edit-modal__actions">
                      <button type="button" className="shift-edit-modal__btn shift-edit-modal__btn--cancel" onClick={() => setConfirm(null)} disabled={confirm.busy}>Cancel</button>
                      <button type="button" className="shift-edit-modal__btn shift-edit-modal__btn--danger" onClick={() => doDeactivate(s)} disabled={confirm.busy || up === null}>
                        {confirm.busy ? 'Deactivating…' : 'Deactivate'}
                      </button>
                    </div>
                  </li>
                )}
              </React.Fragment>
            );
          })}
          {!active.length && <li className="tm-empty">Nobody is active.</li>}
        </ul>

        {inactiveList.length > 0 && (
          <>
            <div className="tm-bar tm-bar--inactive"><span className="tm-bar__lbl">Inactive · {inactiveList.length}</span></div>
            <ul className="tm-list tm-list--inactive">
              {inactiveList.map(s => {
                const info = inactive[s.uid] || {};
                const since = info.at?.toDate ? info.at.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                return (
                  <li key={s.uid} className="tm-row tm-row--inactive">
                    <span className="tm-avatar" style={avatarStyle(s)}>{initials(s.name)}</span>
                    <span className="tm-info">
                      <span className="tm-name">{nameOf(s)}</span>
                      <span className="tm-role">Inactive{since ? ` since ${since}` : ''}{info.byName ? ` · by ${info.byName}` : ''}</span>
                    </span>
                    {canDeactivate && (
                      <button type="button" className="tm-react" onClick={() => onReactivate(s)}>Reactivate</button>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {!canDeactivate && (
          <p className="tm-foot">Only an owner or admin can deactivate or reactivate people.</p>
        )}
      </div>
    </div>
  );
}
